import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './GrantList.css';

const GrantList = ({ userInfo }) => {
    const [grants, setGrants] = useState([]);
    const [personnel, setPersonnel] = useState([]);
    const [activeGrantTab, setActiveGrantTab] = useState(null);
    const [expenditureData, setExpenditureData] = useState({});
    const [loadingExpenditure, setLoadingExpenditure] = useState(false);

    // Form state for new grant
    const [newGrant, setNewGrant] = useState({
        'Grant/Contract Number': '',
        'Grant Nickname': '',
        'Project Title': '',
        'Principal Investigator': '',
        'Grant Contact': '',
        'Project Start Date': '',
        'Project End Date': '',
        'Materials': '',
        'Travel': '',
        'Overhead': '',
        'Sub-Contract': '',
        'Equipment': '',
        'Fee': '',
        'Personnel Budgets': {},
        'Total Budget': '',
        'Is Active': 'Yes',
    });

    const [editGrant, setEditGrant] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showInactiveGrants, setShowInactiveGrants] = useState(false);
    const [showAddGrantPopup, setShowAddGrantPopup] = useState(false);
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [checkedPersonnel, setCheckedPersonnel] = useState({});
    const [newCategoryName, setNewCategoryName] = useState('');

    // Monthly cycle settings state
    const [showMonthlyCycleModal, setShowMonthlyCycleModal] = useState(false);
    const [monthlyCycleSettings, setMonthlyCycleSettings] = useState({
        cycleStartMonth: null,
        isPaused: false
    });
    const [selectedStartYear, setSelectedStartYear] = useState(new Date().getFullYear());
    const [selectedStartMonth, setSelectedStartMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
    const [savingCycleSettings, setSavingCycleSettings] = useState(false);

    const isAdmin = userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin';

    // Helper to safely parse Personnel Budgets (may be JSON string from CSV)
    const parsePersonnelBudgets = (budgets) => {
        if (!budgets) return {};
        if (typeof budgets === 'string') {
            try { return JSON.parse(budgets); } catch { return {}; }
        }
        return budgets;
    };

    const monthNames = [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    const getAvailableYears = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        return [currentYear, currentYear - 1, currentYear - 2];
    };

    const availableYears = getAvailableYears();

    useEffect(() => {
        fetchGrants();
        fetchPersonnel();
        if (isAdmin) {
            fetchMonthlyCycleSettings();
        }
        const fetchExpenseTypes = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/expense-types`,
                    { withCredentials: true }
                );
                setExpenseTypes(response.data || []);
            } catch (error) {
                console.error('Error fetching expense types:', error);
            }
        };
        fetchExpenseTypes();
    }, [isAdmin]);

    // Set first active grant as default tab
    useEffect(() => {
        const activeGrants = grants.filter(g => g['Is Active'] === 'Yes');
        if (activeGrants.length > 0 && !activeGrantTab) {
            setActiveGrantTab(activeGrants[0]['Grant/Contract Number']);
        }
    }, [grants, activeGrantTab]);

    // Fetch expenditure data when active tab changes
    useEffect(() => {
        if (activeGrantTab) {
            fetchExpenditureData(activeGrantTab);
        }
    }, [activeGrantTab]);

    const fetchPersonnel = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_API_URL}/personnel`,
                { withCredentials: true }
            );
            setPersonnel(response.data || []);
        } catch (error) {
            console.error('Error fetching personnel:', error);
        }
    };

    const fetchExpenditureData = useCallback(async (grantNumber) => {
        setLoadingExpenditure(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_API_URL}/grants/report/${encodeURIComponent(grantNumber)}`,
                { withCredentials: true }
            );
            setExpenditureData(prev => ({
                ...prev,
                [grantNumber]: response.data
            }));
        } catch (error) {
            console.error('Error fetching expenditure data:', error);
            // Set empty data on error
            setExpenditureData(prev => ({
                ...prev,
                [grantNumber]: { categories: {}, personnelBreakdown: {} }
            }));
        } finally {
            setLoadingExpenditure(false);
        }
    }, []);

    const fetchMonthlyCycleSettings = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
                { withCredentials: true }
            );
            if (response.data) {
                setMonthlyCycleSettings(response.data);
                if (response.data.cycleStartMonth) {
                    const [year, month] = response.data.cycleStartMonth.split('-');
                    setSelectedStartYear(parseInt(year));
                    setSelectedStartMonth(month);
                }
            }
        } catch (error) {
            console.error('Error fetching monthly cycle settings:', error);
        }
    };

    const handleSaveMonthlyCycleSettings = async () => {
        setSavingCycleSettings(true);
        try {
            const cycleStartMonth = `${selectedStartYear}-${selectedStartMonth}`;
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
                { cycleStartMonth, isPaused: monthlyCycleSettings.isPaused },
                { withCredentials: true }
            );
            if (response.data.success) {
                setMonthlyCycleSettings(response.data.settings);
                setShowMonthlyCycleModal(false);
                alert('Monthly cycle settings saved successfully!');
            }
        } catch (error) {
            console.error('Error saving monthly cycle settings:', error);
            alert('Failed to save monthly cycle settings. Please try again.');
        } finally {
            setSavingCycleSettings(false);
        }
    };

    const handleTogglePause = async () => {
        setSavingCycleSettings(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
                { isPaused: !monthlyCycleSettings.isPaused },
                { withCredentials: true }
            );
            if (response.data.success) {
                setMonthlyCycleSettings(response.data.settings);
                alert(`Monthly cycle ${response.data.settings.isPaused ? 'paused' : 'resumed'} successfully!`);
            }
        } catch (error) {
            console.error('Error toggling monthly cycle pause:', error);
            alert('Failed to update monthly cycle. Please try again.');
        } finally {
            setSavingCycleSettings(false);
        }
    };

    const fetchGrants = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/grants`, { withCredentials: true });
            const filteredGrants = response.data.filter(grant => !isEmptyGrant(grant));
            setGrants(filteredGrants);
        } catch (error) {
            console.error('Error fetching grants:', error);
        }
    };

    const handleAddNewCategory = async () => {
        const name = newCategoryName.trim();
        if (!name) return;
        if (expenseTypes.includes(name)) {
            alert('This expense type already exists.');
            return;
        }
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/expense-types`,
                { type: name },
                { withCredentials: true }
            );
            setExpenseTypes(prev => [...prev, name]);
            setNewCategoryName('');
        } catch (error) {
            console.error('Error adding expense type:', error);
            alert('Failed to add expense type.');
        }
    };

    const activeGrants = grants.filter(grant => grant['Is Active'] === 'Yes');
    const inactiveGrants = grants.filter(grant => grant['Is Active'] === 'No');

    const isEmptyGrant = (grant) => {
        return Object.values(grant).every(value =>
            value === '' || value === 'N/A' || value === undefined || value === null
        );
    };

    const calculateTotalBudget = (grant) => {
        const budgets = parsePersonnelBudgets(grant['Personnel Budgets']);
        const personnelTotal = Object.values(budgets).reduce(
            (sum, val) => sum + (parseFloat(val) || 0), 0
        );
        let otherTotal = 0;
        const defaultTypes = ['Materials', 'Travel', 'Overhead', 'Sub-Contract', 'Equipment', 'Fee'];
        const allTypes = [...new Set([...defaultTypes, ...expenseTypes])];
        // Map new field names to all possible legacy/CSV column names
        const legacyMap = {
            'Materials': ['Materials & Supplies'],
            'Overhead': ['Overhead/Facilities & Administrative', 'Indirect Costs'],
            'Sub-Contract': ['Subawards/Consortium/Contractual Costs', 'Sub-Contracts'],
        };
        allTypes.forEach(type => {
            const val = parseFloat(grant[type]);
            if (!isNaN(val)) {
                otherTotal += val;
            } else {
                // Fall back to legacy field names only if new name has no value
                const legacyNames = legacyMap[type] || [];
                for (const legacy of legacyNames) {
                    const legacyVal = parseFloat(grant[legacy]);
                    if (!isNaN(legacyVal)) {
                        otherTotal += legacyVal;
                        break; // Only count from one legacy name
                    }
                }
            }
        });
        return personnelTotal + otherTotal;
    };

    const handleAddGrant = async () => {
        if (!newGrant['Grant Contact'] || newGrant['Grant Contact'].trim() === '') {
            alert('Grant Contact is required.');
            return;
        }

        // Calculate total budget
        const totalBudget = calculateTotalBudget(newGrant);
        const parsedBudgets = parsePersonnelBudgets(newGrant['Personnel Budgets']);
        const grantToSubmit = {
            ...newGrant,
            'Total Budget': totalBudget.toString(),
            'Personnel Costs': Object.values(parsedBudgets).reduce(
                (sum, val) => sum + (parseFloat(val) || 0), 0
            ).toString(),
            'Materials & Supplies': newGrant['Materials'] || '0',
            'Travel': newGrant['Travel'] || '0',
            'Overhead/Facilities & Administrative': newGrant['Overhead'] || '0',
            'Subawards/Consortium/Contractual Costs': newGrant['Sub-Contract'] || '0',
            'Equipment': newGrant['Equipment'] || '0',
            'Fee': newGrant['Fee'] || '0',
            'Other': '0',
        };

        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/grants`, grantToSubmit, { withCredentials: true });
            setNewGrant({
                'Grant/Contract Number': '',
                'Grant Nickname': '',
                'Project Title': '',
                'Principal Investigator': '',
                'Grant Contact': '',
                'Project Start Date': '',
                'Project End Date': '',
                'Materials': '',
                'Travel': '',
                'Overhead': '',
                'Sub-Contract': '',
                'Equipment': '',
                'Fee': '',
                'Personnel Budgets': {},
                'Total Budget': '',
                'Is Active': 'Yes',
            });
            setCheckedPersonnel({});
            fetchGrants();
            setShowAddGrantPopup(false);
        } catch (error) {
            console.error('Error adding grant:', error);
            alert('Failed to add grant. Ensure the Grant/Contract Number is unique.');
        }
    };

    const handleEditGrant = (grant) => {
        const parsedBudgets = parsePersonnelBudgets(grant['Personnel Budgets']);
        setEditGrant({...grant, 'Personnel Budgets': parsedBudgets});
        setIsEditing(true);
        // Initialize checked personnel from existing budgets
        const checked = {};
        Object.entries(parsedBudgets).forEach(([name, value]) => {
            if (value && (parseFloat(value) > 0 || value !== '0')) {
                checked[name] = true;
            }
        });
        setCheckedPersonnel(checked);
    };

    const handleUpdateGrant = async () => {
        try {
            const totalBudget = calculateTotalBudget(editGrant);
            const parsedBudgets = parsePersonnelBudgets(editGrant['Personnel Budgets']);
            const personnelTotal = Object.values(parsedBudgets).reduce(
                (sum, val) => sum + (parseFloat(val) || 0), 0
            );

            // Only include checked personnel in the budgets we submit
            const filteredBudgets = {};
            Object.entries(parsedBudgets).forEach(([name, value]) => {
                if (checkedPersonnel[name] && parseFloat(value) > 0) {
                    filteredBudgets[name] = value;
                }
            });

            const grantToSubmit = {
                ...editGrant,
                'Total Budget': totalBudget.toString(),
                'Personnel Costs': personnelTotal.toString(),
                'Personnel Budgets': filteredBudgets,
                // Map new field names to backend CSV column names
                'Materials & Supplies': editGrant['Materials'] || editGrant['Materials & Supplies'] || '0',
                'Overhead/Facilities & Administrative': editGrant['Overhead'] || editGrant['Overhead/Facilities & Administrative'] || '0',
                'Subawards/Consortium/Contractual Costs': editGrant['Sub-Contract'] || editGrant['Subawards/Consortium/Contractual Costs'] || '0',
            };

            await axios.put(
                `${process.env.REACT_APP_BACKEND_API_URL}/grants/${editGrant['Grant/Contract Number']}`,
                grantToSubmit,
                { withCredentials: true }
            );
            setEditGrant(null);
            setIsEditing(false);
            fetchGrants();
            // Refresh expenditure data for the updated grant
            if (activeGrantTab === editGrant['Grant/Contract Number']) {
                fetchExpenditureData(activeGrantTab);
            }
        } catch (error) {
            console.error('Error updating grant:', error);
            alert(`Failed to update grant: ${error.response?.data || error.message}`);
        }
    };

    const handleCancelEdit = () => {
        setEditGrant(null);
        setIsEditing(false);
    };

    const handleCancelAdd = () => {
        setShowAddGrantPopup(false);
    };

    const formatCurrency = (value) => {
        const num = parseFloat(value) || 0;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(num);
    };

    const renderGrantTab = (grant) => {
        const grantNumber = grant['Grant/Contract Number'];
        const expData = expenditureData[grantNumber] || { categories: {}, personnelBreakdown: {} };

        // Get personnel budgets from grant or default to empty
        const personnelBudgets = parsePersonnelBudgets(grant['Personnel Budgets']);
        const personnelExpenses = expData.personnelBreakdown || {};

        // Calculate totals
        const totalPersonnelBudget = Object.values(personnelBudgets).reduce(
            (sum, val) => sum + (parseFloat(val) || 0), 0
        ) || parseFloat(grant['Personnel Costs']) || 0;

        const totalPersonnelExpended = Object.values(personnelExpenses).reduce(
            (sum, p) => sum + (p.amount || 0), 0
        );

        const materialsSuppliesBudget = parseFloat(grant['Materials'] || grant['Materials & Supplies']) || 0;
        const materialsSuppliesExpended = expData.categories?.['Materials']?.['Expended to Date'] || expData.categories?.['Materials & Supplies']?.['Expended to Date'] || 0;

        const travelBudget = parseFloat(grant['Travel'] || grant['TABA Funding']) || 0;
        const travelExpended = expData.categories?.['Travel']?.['Expended to Date'] || 0;

        // Equipment and Fee budgets
        const equipmentBudget = parseFloat(grant['Equipment']) || 0;
        const equipmentExpended = expData.categories?.['Equipment']?.['Expended to Date'] || 0;

        const feeBudget = parseFloat(grant['Fee']) || 0;
        const feeExpended = expData.categories?.['Fee']?.['Expended to Date'] || 0;

        // Custom categories - collect any categories not in default list
        const defaultCategories = ['Materials', 'Materials & Supplies', 'Travel', 'TABA Funding', 'Equipment', 'Fee', 'Overhead', 'Overhead/Facilities & Administrative', 'Sub-Contract', 'Sub-Contracts', 'Subawards/Consortium/Contractual Costs', 'Indirect Costs'];
        const customCategoryTotals = expenseTypes
            .filter(type => !defaultCategories.includes(type))
            .map(type => ({
                name: type,
                budget: parseFloat(grant[type]) || 0,
                expended: expData.categories?.[type]?.['Expended to Date'] || 0
            }));
        const totalCustomBudget = customCategoryTotals.reduce((sum, c) => sum + c.budget, 0);
        const totalCustomExpended = customCategoryTotals.reduce((sum, c) => sum + c.expended, 0);

        const totalCurrentExpenseBudget = materialsSuppliesBudget + travelBudget + equipmentBudget + feeBudget + totalCustomBudget;
        const totalCurrentExpenseExpended = materialsSuppliesExpended + travelExpended + equipmentExpended + feeExpended + totalCustomExpended;

        const totalDirectCostsBudget = totalPersonnelBudget + totalCurrentExpenseBudget;
        const totalDirectCostsExpended = totalPersonnelExpended + totalCurrentExpenseExpended;

        const overheadBudget = parseFloat(grant['Overhead'] || grant['Indirect Costs'] || grant['Overhead/Facilities & Administrative']) || 0;
        const overheadExpended = expData.categories?.['Overhead']?.['Expended to Date'] || expData.categories?.['Overhead/Facilities & Administrative']?.['Expended to Date'] || 0;

        const subTotalBudget = totalDirectCostsBudget + overheadBudget;
        const subTotalExpended = totalDirectCostsExpended + overheadExpended;

        const subContractsBudget = parseFloat(grant['Sub-Contract'] || grant['Sub-Contracts'] || grant['Subawards/Consortium/Contractual Costs']) || 0;
        const subContractsExpended = expData.categories?.['Sub-Contract']?.['Expended to Date'] || expData.categories?.['Subawards/Consortium/Contractual Costs']?.['Expended to Date'] || 0;

        const finalTotalBudget = subTotalBudget + subContractsBudget;
        const finalTotalExpended = subTotalExpended + subContractsExpended;

        // Calculate indirect rate stats
        const indirectBase = totalDirectCostsExpended - feeExpended - equipmentExpended;
        const indirectFederalShare = overheadExpended;
        const indirectRate = indirectBase > 0 ? (indirectFederalShare / indirectBase * 100) : 0;

        return (
            <div className="grant-tab-content">
                {/* Project Information */}
                <div className="project-info-section">
                    <h3>Project Information</h3>
                    <div className="project-info-grid">
                        <div className="info-item">
                            <label>Grant/Contract Number:</label>
                            <span>{grant['Grant/Contract Number']}</span>
                        </div>
                        {grant['Grant Nickname'] && (
                            <div className="info-item">
                                <label>Grant Nickname:</label>
                                <span>{grant['Grant Nickname']}</span>
                            </div>
                        )}
                        <div className="info-item">
                            <label>Project Title:</label>
                            <span>{grant['Project Title']}</span>
                        </div>
                        <div className="info-item">
                            <label>Principal Investigator:</label>
                            <span>{grant['Principal Investigator']}</span>
                        </div>
                        <div className="info-item">
                            <label>Grant Contact:</label>
                            <span>{grant['Grant Contact']}</span>
                        </div>
                        <div className="info-item">
                            <label>Project Start Date:</label>
                            <span>{grant['Project Start Date']}</span>
                        </div>
                        <div className="info-item">
                            <label>Project End Date:</label>
                            <span>{grant['Project End Date']}</span>
                        </div>
                    </div>
                    <button className="edit-button" onClick={() => handleEditGrant(grant)}>
                        Edit Grant
                    </button>
                </div>

                {/* Cost Table */}
                <div className="cost-table-section">
                    <h3>Cost Information</h3>
                    {loadingExpenditure ? (
                        <div className="loading-spinner">Loading expenditure data...</div>
                    ) : (
                        <table className="cost-table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th>Current Budget</th>
                                    <th>Expended Project to Date</th>
                                    <th>Budget Balance Available</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Personnel Rows - only show personnel with budget or expenditures */}
                                {(() => {
                                    // Collect all personnel names that have a budget or expenditures for this grant
                                    const relevantNames = new Set([
                                        ...Object.keys(personnelBudgets).filter(n => parseFloat(personnelBudgets[n]) > 0),
                                        ...Object.keys(personnelExpenses).filter(n => personnelExpenses[n]?.amount > 0),
                                    ]);
                                    return [...relevantNames].map(personName => {
                                        const budget = parseFloat(personnelBudgets[personName]) || 0;
                                        const expended = personnelExpenses[personName]?.amount || 0;
                                        return (
                                            <tr key={personName} className="personnel-row">
                                                <td>{personName}</td>
                                                <td>{formatCurrency(budget)}</td>
                                                <td>{formatCurrency(expended)}</td>
                                                <td className={budget - expended < 0 ? 'negative' : ''}>
                                                    {formatCurrency(budget - expended)}
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()}

                                {/* Total Salaries + EB + Taxes */}
                                <tr className="subtotal-row">
                                    <td><strong>Total Salaries + EB + Taxes</strong></td>
                                    <td><strong>{formatCurrency(totalPersonnelBudget)}</strong></td>
                                    <td><strong>{formatCurrency(totalPersonnelExpended)}</strong></td>
                                    <td className={totalPersonnelBudget - totalPersonnelExpended < 0 ? 'negative' : ''}>
                                        <strong>{formatCurrency(totalPersonnelBudget - totalPersonnelExpended)}</strong>
                                    </td>
                                </tr>

                                {/* Spacer row */}
                                <tr className="spacer-row"><td colSpan="4"></td></tr>

                                {/* Materials */}
                                <tr>
                                    <td>Materials</td>
                                    <td>{formatCurrency(materialsSuppliesBudget)}</td>
                                    <td>{formatCurrency(materialsSuppliesExpended)}</td>
                                    <td className={materialsSuppliesBudget - materialsSuppliesExpended < 0 ? 'negative' : ''}>
                                        {formatCurrency(materialsSuppliesBudget - materialsSuppliesExpended)}
                                    </td>
                                </tr>

                                {/* Travel */}
                                <tr>
                                    <td>Travel</td>
                                    <td>{formatCurrency(travelBudget)}</td>
                                    <td>{formatCurrency(travelExpended)}</td>
                                    <td className={travelBudget - travelExpended < 0 ? 'negative' : ''}>
                                        {formatCurrency(travelBudget - travelExpended)}
                                    </td>
                                </tr>

                                {/* Equipment */}
                                <tr>
                                    <td>Equipment</td>
                                    <td>{formatCurrency(equipmentBudget)}</td>
                                    <td>{formatCurrency(equipmentExpended)}</td>
                                    <td className={equipmentBudget - equipmentExpended < 0 ? 'negative' : ''}>
                                        {formatCurrency(equipmentBudget - equipmentExpended)}
                                    </td>
                                </tr>

                                {/* Fee */}
                                <tr>
                                    <td>Fee</td>
                                    <td>{formatCurrency(feeBudget)}</td>
                                    <td>{formatCurrency(feeExpended)}</td>
                                    <td className={feeBudget - feeExpended < 0 ? 'negative' : ''}>
                                        {formatCurrency(feeBudget - feeExpended)}
                                    </td>
                                </tr>

                                {/* Custom Categories */}
                                {customCategoryTotals.map(cat => (
                                    <tr key={cat.name}>
                                        <td>{cat.name}</td>
                                        <td>{formatCurrency(cat.budget)}</td>
                                        <td>{formatCurrency(cat.expended)}</td>
                                        <td className={cat.budget - cat.expended < 0 ? 'negative' : ''}>
                                            {formatCurrency(cat.budget - cat.expended)}
                                        </td>
                                    </tr>
                                ))}

                                {/* Total Current Expense */}
                                <tr className="subtotal-row">
                                    <td><strong>Total Current Expense</strong></td>
                                    <td><strong>{formatCurrency(totalCurrentExpenseBudget)}</strong></td>
                                    <td><strong>{formatCurrency(totalCurrentExpenseExpended)}</strong></td>
                                    <td className={totalCurrentExpenseBudget - totalCurrentExpenseExpended < 0 ? 'negative' : ''}>
                                        <strong>{formatCurrency(totalCurrentExpenseBudget - totalCurrentExpenseExpended)}</strong>
                                    </td>
                                </tr>

                                {/* Spacer row */}
                                <tr className="spacer-row"><td colSpan="4"></td></tr>

                                {/* Total Direct Costs */}
                                <tr className="total-direct-row">
                                    <td><strong>Total Direct Costs</strong></td>
                                    <td><strong>{formatCurrency(totalDirectCostsBudget)}</strong></td>
                                    <td><strong>{formatCurrency(totalDirectCostsExpended)}</strong></td>
                                    <td className={totalDirectCostsBudget - totalDirectCostsExpended < 0 ? 'negative' : ''}>
                                        <strong>{formatCurrency(totalDirectCostsBudget - totalDirectCostsExpended)}</strong>
                                    </td>
                                </tr>

                                {/* Spacer row */}
                                <tr className="spacer-row"><td colSpan="4"></td></tr>

                                {/* Overhead */}
                                <tr>
                                    <td>Overhead</td>
                                    <td>{formatCurrency(overheadBudget)}</td>
                                    <td>{formatCurrency(overheadExpended)}</td>
                                    <td className={overheadBudget - overheadExpended < 0 ? 'negative' : ''}>
                                        {formatCurrency(overheadBudget - overheadExpended)}
                                    </td>
                                </tr>

                                {/* SUB-Total */}
                                <tr className="subtotal-row">
                                    <td><strong>Sub-Total</strong></td>
                                    <td><strong>{formatCurrency(subTotalBudget)}</strong></td>
                                    <td><strong>{formatCurrency(subTotalExpended)}</strong></td>
                                    <td className={subTotalBudget - subTotalExpended < 0 ? 'negative' : ''}>
                                        <strong>{formatCurrency(subTotalBudget - subTotalExpended)}</strong>
                                    </td>
                                </tr>

                                {/* Spacer row */}
                                <tr className="spacer-row"><td colSpan="4"></td></tr>

                                {/* Sub-Contract */}
                                <tr>
                                    <td>Sub-Contract</td>
                                    <td>{formatCurrency(subContractsBudget)}</td>
                                    <td>{formatCurrency(subContractsExpended)}</td>
                                    <td className={subContractsBudget - subContractsExpended < 0 ? 'negative' : ''}>
                                        {formatCurrency(subContractsBudget - subContractsExpended)}
                                    </td>
                                </tr>

                                {/* FINAL TOTAL */}
                                <tr className="final-total-row">
                                    <td><strong>TOTAL</strong></td>
                                    <td><strong>{formatCurrency(finalTotalBudget)}</strong></td>
                                    <td><strong>{formatCurrency(finalTotalExpended)}</strong></td>
                                    <td className={finalTotalBudget - finalTotalExpended < 0 ? 'negative' : ''}>
                                        <strong>{formatCurrency(finalTotalBudget - finalTotalExpended)}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Indirect Rate Stats */}
                <div className="indirect-stats-section">
                    <h3>Indirect Rate Statistics</h3>
                    <div className="stats-grid">
                        <div className="stat-item">
                            <label>Indirect Base:</label>
                            <span>{formatCurrency(indirectBase)}</span>
                            <small>(Total Direct Spent - Fee Spent - Equipment Spent)</small>
                        </div>
                        <div className="stat-item">
                            <label>Indirect Federal Share:</label>
                            <span>{formatCurrency(indirectFederalShare)}</span>
                            <small>(Indirect Expended to Date)</small>
                        </div>
                        <div className="stat-item">
                            <label>Indirect Rate:</label>
                            <span>{indirectRate.toFixed(2)}%</span>
                            <small>(Indirect Federal Share / Indirect Base)</small>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderAddGrantForm = () => {
        const handlePersonnelBudgetChange = (personName, value) => {
            setNewGrant(prev => ({
                ...prev,
                'Personnel Budgets': {
                    ...prev['Personnel Budgets'],
                    [personName]: value
                }
            }));
        };

        const totalBudget = calculateTotalBudget(newGrant);

        return (
            <div className="add-grant-form">
                {/* Project Information Section */}
                <div className="form-section">
                    <h4>Project Information</h4>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Grant/Contract Number *</label>
                            <input
                                type="text"
                                value={newGrant['Grant/Contract Number']}
                                onChange={(e) => setNewGrant(prev => ({...prev, 'Grant/Contract Number': e.target.value}))}
                            />
                        </div>
                        <div className="form-group">
                            <label>Grant Nickname</label>
                            <input
                                type="text"
                                maxLength={20}
                                placeholder="Short name (max 20 chars)"
                                value={newGrant['Grant Nickname'] || ''}
                                onChange={(e) => setNewGrant(prev => ({...prev, 'Grant Nickname': e.target.value}))}
                            />
                        </div>
                        <div className="form-group">
                            <label>Project Title</label>
                            <input
                                type="text"
                                value={newGrant['Project Title']}
                                onChange={(e) => setNewGrant(prev => ({...prev, 'Project Title': e.target.value}))}
                            />
                        </div>
                        <div className="form-group">
                            <label>Principal Investigator</label>
                            <input
                                type="text"
                                value={newGrant['Principal Investigator']}
                                onChange={(e) => setNewGrant(prev => ({...prev, 'Principal Investigator': e.target.value}))}
                            />
                        </div>
                        <div className="form-group">
                            <label>Grant Contact *</label>
                            <input
                                type="text"
                                value={newGrant['Grant Contact']}
                                onChange={(e) => setNewGrant(prev => ({...prev, 'Grant Contact': e.target.value}))}
                            />
                        </div>
                        <div className="form-group">
                            <label>Project Start Date</label>
                            <input
                                type="date"
                                value={newGrant['Project Start Date']}
                                onChange={(e) => setNewGrant(prev => ({...prev, 'Project Start Date': e.target.value}))}
                            />
                        </div>
                        <div className="form-group">
                            <label>Project End Date</label>
                            <input
                                type="date"
                                value={newGrant['Project End Date']}
                                onChange={(e) => setNewGrant(prev => ({...prev, 'Project End Date': e.target.value}))}
                            />
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <select
                                value={newGrant['Is Active']}
                                onChange={(e) => setNewGrant(prev => ({...prev, 'Is Active': e.target.value}))}
                            >
                                <option value="Yes">Active</option>
                                <option value="No">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Personnel Budget Section */}
                <div className="form-section">
                    <h4>Personnel Budgets</h4>
                    <p className="form-hint">Check personnel to include, then enter budget allocation.</p>
                    <table className="personnel-budget-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                <th style={{ textAlign: 'left', padding: '8px', width: '40px' }}></th>
                                <th style={{ textAlign: 'left', padding: '8px' }}>Name</th>
                                <th style={{ textAlign: 'right', padding: '8px', width: '150px' }}>Budget</th>
                            </tr>
                        </thead>
                        <tbody>
                            {personnel.filter(p => p['Employment status'] === 'Active').map(person => (
                                <tr key={person['Name']} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '6px 8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={!!checkedPersonnel[person['Name']]}
                                            onChange={(e) => {
                                                setCheckedPersonnel(prev => ({ ...prev, [person['Name']]: e.target.checked }));
                                                if (!e.target.checked) {
                                                    handlePersonnelBudgetChange(person['Name'], '');
                                                }
                                            }}
                                        />
                                    </td>
                                    <td style={{ padding: '6px 8px' }}>{person['Name']}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                        {checkedPersonnel[person['Name']] ? (
                                            <input
                                                type="number"
                                                placeholder="$0.00"
                                                style={{ width: '130px', textAlign: 'right' }}
                                                value={newGrant['Personnel Budgets'][person['Name']] || ''}
                                                onChange={(e) => handlePersonnelBudgetChange(person['Name'], e.target.value)}
                                            />
                                        ) : (
                                            <span style={{ color: '#9ca3af' }}>--</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Other Costs Section */}
                <div className="form-section">
                    <h4>Other Costs</h4>
                    <div className="form-grid">
                        {expenseTypes.map(type => (
                            <div key={type} className="form-group">
                                <label>{type}</label>
                                <input
                                    type="number"
                                    placeholder="$0.00"
                                    value={newGrant[type] || ''}
                                    onChange={(e) => setNewGrant(prev => ({...prev, [type]: e.target.value}))}
                                />
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="New category name"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddNewCategory()}
                            style={{ flex: 1, maxWidth: '200px' }}
                        />
                        <button type="button" className="btn-secondary" onClick={handleAddNewCategory} style={{ fontSize: '13px', padding: '4px 12px' }}>
                            + Add Category
                        </button>
                    </div>
                </div>

                {/* Total Budget Display */}
                <div className="form-section total-budget-section">
                    <div className="total-budget-display">
                        <label>Total Budget:</label>
                        <span className="total-amount">{formatCurrency(totalBudget)}</span>
                    </div>
                </div>
            </div>
        );
    };

    const renderEditGrantForm = () => {
        if (!editGrant) return null;

        const handleFieldChange = (field, value) => {
            setEditGrant(prev => ({ ...prev, [field]: value }));
        };

        const handleEditPersonnelBudgetChange = (personName, value) => {
            setEditGrant(prev => ({
                ...prev,
                'Personnel Budgets': {
                    ...prev['Personnel Budgets'],
                    [personName]: value
                }
            }));
        };

        // Calculate total budget for edit form - reuse shared function
        const calculateEditTotalBudget = () => {
            return calculateTotalBudget(editGrant);
        };

        const totalBudget = calculateEditTotalBudget();

        return (
            <div className="add-grant-form">
                {/* Project Information Section */}
                <div className="form-section">
                    <h4>Project Information</h4>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Grant/Contract Number</label>
                            <input
                                type="text"
                                value={editGrant['Grant/Contract Number']}
                                disabled
                            />
                        </div>
                        <div className="form-group">
                            <label>Grant Nickname</label>
                            <input
                                type="text"
                                maxLength={20}
                                placeholder="Short name (max 20 chars)"
                                value={editGrant['Grant Nickname'] || ''}
                                onChange={(e) => handleFieldChange('Grant Nickname', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Project Title</label>
                            <input
                                type="text"
                                value={editGrant['Project Title'] || ''}
                                onChange={(e) => handleFieldChange('Project Title', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Principal Investigator</label>
                            <input
                                type="text"
                                value={editGrant['Principal Investigator'] || ''}
                                onChange={(e) => handleFieldChange('Principal Investigator', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Grant Contact</label>
                            <input
                                type="text"
                                value={editGrant['Grant Contact'] || ''}
                                onChange={(e) => handleFieldChange('Grant Contact', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Project Start Date</label>
                            <input
                                type="date"
                                value={editGrant['Project Start Date'] || ''}
                                onChange={(e) => handleFieldChange('Project Start Date', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Project End Date</label>
                            <input
                                type="date"
                                value={editGrant['Project End Date'] || ''}
                                onChange={(e) => handleFieldChange('Project End Date', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <select
                                value={editGrant['Is Active'] || 'Yes'}
                                onChange={(e) => handleFieldChange('Is Active', e.target.value)}
                            >
                                <option value="Yes">Active</option>
                                <option value="No">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Personnel Budget Section */}
                <div className="form-section">
                    <h4>Personnel Budgets</h4>
                    <p className="form-hint">Check personnel to include, then enter budget allocation.</p>
                    <table className="personnel-budget-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                <th style={{ textAlign: 'left', padding: '8px', width: '40px' }}></th>
                                <th style={{ textAlign: 'left', padding: '8px' }}>Name</th>
                                <th style={{ textAlign: 'right', padding: '8px', width: '150px' }}>Budget</th>
                            </tr>
                        </thead>
                        <tbody>
                            {personnel.filter(p => p['Employment status'] === 'Active').map(person => (
                                <tr key={person['Name']} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '6px 8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={!!checkedPersonnel[person['Name']]}
                                            onChange={(e) => {
                                                setCheckedPersonnel(prev => ({ ...prev, [person['Name']]: e.target.checked }));
                                                if (!e.target.checked) {
                                                    handleEditPersonnelBudgetChange(person['Name'], '');
                                                }
                                            }}
                                        />
                                    </td>
                                    <td style={{ padding: '6px 8px' }}>{person['Name']}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                        {checkedPersonnel[person['Name']] ? (
                                            <input
                                                type="number"
                                                placeholder="$0.00"
                                                style={{ width: '130px', textAlign: 'right' }}
                                                value={editGrant['Personnel Budgets']?.[person['Name']] || ''}
                                                onChange={(e) => handleEditPersonnelBudgetChange(person['Name'], e.target.value)}
                                            />
                                        ) : (
                                            <span style={{ color: '#9ca3af' }}>--</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Other Costs Section */}
                <div className="form-section">
                    <h4>Other Costs</h4>
                    <div className="form-grid">
                        {expenseTypes.map(type => {
                            const legacyMap = {
                                'Materials': 'Materials & Supplies',
                                'Travel': 'Travel',
                                'Overhead': 'Overhead/Facilities & Administrative',
                                'Sub-Contract': 'Subawards/Consortium/Contractual Costs',
                            };
                            const legacyField = legacyMap[type];
                            const value = editGrant[type] || (legacyField ? editGrant[legacyField] : '') || '';
                            return (
                                <div key={type} className="form-group">
                                    <label>{type}</label>
                                    <input
                                        type="number"
                                        placeholder="$0.00"
                                        value={value}
                                        onChange={(e) => {
                                            handleFieldChange(type, e.target.value);
                                            // Clear legacy field to prevent double-counting
                                            if (legacyField && legacyField !== type) handleFieldChange(legacyField, '');
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="New category name"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddNewCategory()}
                            style={{ flex: 1, maxWidth: '200px' }}
                        />
                        <button type="button" className="btn-secondary" onClick={handleAddNewCategory} style={{ fontSize: '13px', padding: '4px 12px' }}>
                            + Add Category
                        </button>
                    </div>
                </div>

                {/* Total Budget Display */}
                <div className="form-section total-budget-section">
                    <div className="total-budget-display">
                        <label>Total Budget:</label>
                        <span className="total-amount">{formatCurrency(totalBudget)}</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="grant-list-container">
            <h1>Grant Reports</h1>

            <div className="action-buttons">
                <button className="add-grant-button" onClick={() => setShowAddGrantPopup(true)}>
                    + Add New Grant
                </button>
            </div>

            {/* Grant Tabs */}
            <div className="grant-tabs-container">
                <div className="grant-tabs">
                    {activeGrants.map(grant => (
                        <button
                            key={grant['Grant/Contract Number']}
                            className={`grant-tab ${activeGrantTab === grant['Grant/Contract Number'] ? 'active' : ''}`}
                            onClick={() => setActiveGrantTab(grant['Grant/Contract Number'])}
                        >
                            {grant['Grant/Contract Number']}
                            <span className="tab-subtitle">{grant['Project Title']}</span>
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="grant-tab-panel">
                    {activeGrants.map(grant => (
                        activeGrantTab === grant['Grant/Contract Number'] && (
                            <div key={grant['Grant/Contract Number']}>
                                {renderGrantTab(grant)}
                            </div>
                        )
                    ))}
                    {activeGrants.length === 0 && (
                        <div className="no-grants-message">
                            <p>No active grants found. Click "Add New Grant" to create one.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Inactive grants collapsible section */}
            <div className="inactive-grants-section">
                <button
                    onClick={() => setShowInactiveGrants(!showInactiveGrants)}
                    className="toggle-inactive-btn"
                >
                    <span className="toggle-icon">
                        {showInactiveGrants ? '▼' : '►'}
                    </span>
                    Inactive Grants ({inactiveGrants.length})
                </button>

                {showInactiveGrants && (
                    <div className="inactive-grants-content">
                        {inactiveGrants.length > 0 ? (
                            <div className="inactive-grants-list">
                                {inactiveGrants.map(grant => (
                                    <div key={grant['Grant/Contract Number']} className="inactive-grant-card">
                                        <div className="inactive-grant-info">
                                            <strong>{grant['Grant/Contract Number']}</strong>
                                            <span>{grant['Project Title']}</span>
                                        </div>
                                        <button
                                            className="edit-button"
                                            onClick={() => handleEditGrant(grant)}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p>No inactive grants found.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Edit Grant Modal */}
            {isEditing && editGrant && (
                <div className="modal-overlay">
                    <div className="modal-container modal-wide">
                        <div className="modal-header">
                            <h3>Edit Grant</h3>
                            <button className="close-button" onClick={handleCancelEdit}>&times;</button>
                        </div>
                        <div className="modal-content">
                            {renderEditGrantForm()}
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-button" onClick={handleCancelEdit}>Cancel</button>
                            <button className="save-button" onClick={handleUpdateGrant}>Update Grant</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Grant Modal */}
            {showAddGrantPopup && (
                <div className="modal-overlay">
                    <div className="modal-container modal-wide">
                        <div className="modal-header">
                            <h3>Add New Grant</h3>
                            <button className="close-button" onClick={handleCancelAdd}>&times;</button>
                        </div>
                        <div className="modal-content">
                            <p className="form-hint" style={{ marginTop: 0 }}>
                                Grants awarded through the Grant Submission Portal are added here
                                automatically when the application is marked as funded — the award
                                form there pre-fills from the grant questionnaire. Use this form for
                                grants that never went through the portal.
                            </p>
                            {renderAddGrantForm()}
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-button" onClick={handleCancelAdd}>Cancel</button>
                            <button className="save-button" onClick={handleAddGrant}>Add Grant</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GrantList;
